import { make } from "vuex-pathify";
import Vue from "vue";
import { Firebase } from "@/services/firebase";

// properties
const state = {
  items: {},
  current: {},
  currentId: null,
  getCache: false,
};

const mutations = {
  ...make.mutations(state),
  add(state, list) {
    state.items.push(list);
  },
  set(state, lists) {
    state.items = lists;
  },
  remove(state, list) {
    state.items.splice(state.items.indexOf(list), 1);
  },
  edit(state, { list, data = {} }) {
    const index = state.items.indexOf(list);
    Vue.set(state.items, index, {
      ...list,
      ...data,
    });
  },
  resetCurrent(state) {
    // reset to the latest published list
    state.currentId = Object.values(state.items).find((item) => {
      return item.status === "published";
    })?.id;
  },
};

const getters = {
  ...make.getters(state),

  /**
   * Gets the last list the current visitor was viewing from local cache
   */
  lastStoredList(state) {
    let stored = localStorage.getItem("docketCurrentListId");
    let available =
      state.items?.[stored] && state.items?.[stored].status === "published";
    return available ? stored : "";
  },

  /**
   * Gets the last modified list in cache
   */
  lastModified(state) {
    let array = Object.values(state?.items || {});
    return (
      array.length &&
      array.reduce((max, item) => (max.votes > item.modified ? max : item))
        ?.modified
    );
  },
};

const actions = {
  async updateCounts({ rootGetters, state, dispatch, rootState }, list) {
    let tasks = Object.values(rootState.tasks.items);
    let total = (tasks || []).filter((task) => {
      return task.list_id == list.id;
    });
    let done = (total || []).filter((task) => {
      return task.done;
    });

    if (
      total.length !== list.counts.total ||
      done.length !== list.counts.done
    ) {
      console.warn("List counts are off... update?");
      // dispatch("patch", {
      //   id: list.id,
      //   counts: {
      //     total: total.length,
      //     done: done.length,
      //   },
      // });
    }
  },

  async cloneToTeam(
    { dispatch, rootGetters, getters, state, commit },
    { list, moduleName }
  ) {
    // fetch tasks from library to make sure they are up to date
    let db = Firebase.firestore();
    let batch = db.batch();

    // add new list
    let doc = getters["dbRef"].doc();
    let insert = {
      title: list.title || "",
      notes: list.notes || "",
      created_at: new Date(),
      created_by: state._sync.userId,
      project_id: docketData.install_id,
      status: "published",
      version: 1,
      counts: {
        total: 0,
        done: 0,
      },
      modified: Firebase.firestore.Timestamp.now().toMillis(),
    };

    // set new list in batch process and update store
    batch.set(doc, insert);
    commit("INSERT_DOC", {
      id: doc.id,
      ...insert,
    });

    // insert new items into project
    (list.tasks || []).forEach(async (data) => {
      // add
      batch = await dispatch(
        "tasks/addTask",
        {
          data: {
            ...data,
            list_id: doc.id,
          },
          batch, // pass in batch reference
          shouldCommit: false, // don't commit yet
        },
        { root: true }
      );
    });

    await batch.commit();

    return doc.id;
  },

  async bindLists({ dispatch, rootGetters, getters, state }) {
    let where = [
      ["project_id", "==", docketData.install_id],
      ["status", "==", "published"],
    ];

    await dispatch("openDBChannel", {
      clauses: {
        where,
        clauses: { limit: 30 },
      },
    });
  },

  async bindArchived({ dispatch, rootGetters, getters, state }) {
    let where = [
      ["project_id", "==", docketData.install_id],
      ["status", "in", ["published", "archived"]],
    ];

    await dispatch("openDBChannel", {
      clauses: {
        where,
        clauses: { limit: 30 },
      },
    });
  },

  async bindTrashed({ dispatch, rootGetters, getters, state }) {
    let where = [
      ["project_id", "==", docketData.install_id],
      ["status", "in", ["published", "trashed"]],
      [
        "modified",
        ">",
        Firebase.firestore.Timestamp.now().toMillis() - 2592000000, // only newer than 30 days ago
      ],
    ];

    await dispatch("openDBChannel", {
      clauses: {
        where,
        clauses: { limit: 30 },
      },
    });
  },

  unbindLists({ dispatch }) {
    dispatch("closeDBChannel");
  },

  async addList({ dispatch, rootGetters, getters }, data) {
    return await dispatch("set", data);
  },

  async editList({ dispatch }, { list, data }) {
    await dispatch("patch", {
      id: list.id,
      ...data,
    });
  },

  /**
   * change list status to trashed
   */
  async trashList({ commit, rootGetters, state, getters }, list) {
    if (!list?.id) {
      delete state.items[id];
    }

    if (list?.status == "trashed") {
      delete state.items[list.id];
      console.warn("This has already been trashed elsewhere");
      return;
    }

    let db = Firebase.firestore();
    let batch = db.batch();

    // trash list
    let insert = {
      status: "trashed",
      updated_at: new Date(),
      updated_by: state._sync.userId,
    };
    batch.update(getters["dbRef"].doc(list.id), insert);
    commit("PATCH_DOC", {
      id: list.id,
      ...insert,
    });

    // update count in project
    batch.update(rootGetters["project/dbRef"], {
      "counts.total": Firebase.firestore.FieldValue.increment(
        -list.counts.total
      ),
      "counts.done": Firebase.firestore.FieldValue.increment(-list.counts.done),
    });
    commit(
      "project/PATCH_DOC",
      {
        counts: {
          total:
            rootGetters["project/project"]?.counts?.total - list.counts.total,
          done: rootGetters["project/project"]?.counts?.done - list.counts.done,
        },
      },
      { root: true }
    );

    // update count in team
    batch.update(rootGetters["team/dbRef"], {
      "counts.total": Firebase.firestore.FieldValue.increment(
        -list.counts.total
      ),
      "counts.done": Firebase.firestore.FieldValue.increment(-list.counts.done),
    });
    commit(
      "team/PATCH_DOC",
      {
        counts: {
          total: rootGetters["team/team"]?.counts?.total - list.counts.total,
          done: rootGetters["team/team"]?.counts?.done - list.counts.done,
        },
      },
      { root: true }
    );

    return await batch.commit();
  },

  /**
   * Archive a list
   */
  async archive({ commit, rootGetters, state, getters, dispatch }, list) {
    if (!list?.id) {
      delete state.items[id];
    }

    if (list?.status == "archived") {
      delete state.items[list.id];
      console.warn("This has already been archived elsewhere");
      return;
    }

    let db = Firebase.firestore();
    let batch = db.batch();

    // trash list
    let insert = {
      status: "archived",
      updated_at: new Date(),
      updated_by: state._sync.userId,
    };
    batch.update(getters["dbRef"].doc(list.id), insert);
    commit("PATCH_DOC", {
      id: list.id,
      ...insert,
    });

    // update count in project
    batch.update(rootGetters["project/dbRef"], {
      "counts.total": Firebase.firestore.FieldValue.increment(
        -list.counts.total
      ),
      "counts.done": Firebase.firestore.FieldValue.increment(-list.counts.done),
    });
    commit(
      "project/PATCH_DOC",
      {
        counts: {
          total:
            rootGetters["project/project"]?.counts?.total - list.counts.total,
          done: rootGetters["project/project"]?.counts?.done - list.counts.done,
        },
      },
      { root: true }
    );

    // update count in team
    batch.update(rootGetters["team/dbRef"], {
      "counts.total": Firebase.firestore.FieldValue.increment(
        -list.counts.total
      ),
      "counts.done": Firebase.firestore.FieldValue.increment(-list.counts.done),
    });
    commit(
      "team/PATCH_DOC",
      {
        counts: {
          total: rootGetters["team/team"]?.counts?.total - list.counts.total,
          done: rootGetters["team/team"]?.counts?.done - list.counts.done,
        },
      },
      { root: true }
    );

    dispatch(
      "user/logEvent",
      {
        name: "list_archived",
      },
      { root: true }
    );

    return await batch.commit();
  },

  /**
   * Change list back to published
   */
  async rePublish({ commit, rootGetters, state, getters, dispatch }, list) {
    if (list?.status === "published") {
      console.warn("This has already been published.");
      return;
    }

    let db = Firebase.firestore();
    let batch = db.batch();

    // unarchive list
    let insert = {
      status: "published",
      updated_at: new Date(),
      updated_by: state._sync.userId,
    };
    batch.update(getters["dbRef"].doc(list.id), insert);
    commit("PATCH_DOC", {
      id: list.id,
      ...insert,
    });

    // update count in project
    batch.update(rootGetters["project/dbRef"], {
      "counts.total": Firebase.firestore.FieldValue.increment(
        list.counts.total
      ),
      "counts.done": Firebase.firestore.FieldValue.increment(list.counts.done),
    });

    commit(
      "project/PATCH_DOC",
      {
        counts: {
          total:
            rootGetters["project/project"]?.counts?.total + list.counts.total,
          done: rootGetters["project/project"]?.counts?.done + list.counts.done,
        },
      },
      { root: true }
    );

    // update count in team
    batch.update(rootGetters["team/dbRef"], {
      "counts.total": Firebase.firestore.FieldValue.increment(
        list.counts.total
      ),
      "counts.done": Firebase.firestore.FieldValue.increment(list.counts.done),
    });

    commit(
      "team/PATCH_DOC",
      {
        counts: {
          total: rootGetters["team/team"]?.counts?.total + list.counts.total,
          done: rootGetters["team/team"]?.counts?.done + list.counts.done,
        },
      },
      { root: true }
    );

    if (list.status == "archived") {
      dispatch(
        "user/logEvent",
        {
          name: "list_unarchived",
        },
        { root: true }
      );
    }
    if (list.status == "trashed") {
      dispatch(
        "user/logEvent",
        {
          name: "list_untrashed",
        },
        { root: true }
      );
    }

    return await batch.commit();
  },
};

export default {
  firestorePath: "teams/{teamId}/teamLists",
  firestoreRefType: "collection",
  moduleName: "lists",
  statePropName: "items",
  sync: {
    debounceTimerMs: window.Cypress ? 1 : 1000,
    defaultValues: {
      project_id: docketData.install_id,
      status: "published",
      version: 1,
      counts: {
        total: 0,
        done: 0,
      },
    },
    insertHook: function(updateStore, doc, store) {
      doc.modified = Firebase.firestore.Timestamp.now().toMillis();
      return updateStore(doc);
    },
    patchHook: function(updateStore, doc, store) {
      doc.modified = Firebase.firestore.Timestamp.now().toMillis();
      return updateStore(doc);
    },
  },
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
